// Importación del inventario desde Excel o CSV en el panel lateral (Mantenimiento M1): plantilla, archivo, relación de
// columnas, vista previa con validación y duplicados, y confirmación antes de guardar.

import React, { useMemo, useState } from 'react';
import { GuardadoVisible, type EstadoGuardado } from '../../components/tablero/Tablero';
import {
  COLUMNAS_IMPORT, descargarLibro, leerHoja, libroPlantilla, mapeoAutomatico, revisarFilas,
  type CampoImport, type ContextoImport,
} from '../../lib/mantenimiento/mant-importar';
import { importarActivos } from '../../lib/mantenimiento/mant-datos';
import { etiquetaCategoria } from '../../lib/mantenimiento/mant-tipos';

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const MAX_PREVIA = 200;

interface Props { companyId: string; contexto: ContextoImport; onImportado: () => void }

export default function ImportarActivos({ companyId, contexto, onImportado }: Props) {
  const [nombre, setNombre] = useState('');
  const [encabezados, setEncabezados] = useState<string[]>([]);
  const [filas, setFilas] = useState<Record<string, unknown>[]>([]);
  const [mapeo, setMapeo] = useState<Record<string, CampoImport | ''>>({});
  const [conAvisos, setConAvisos] = useState(true);
  const [confirmar, setConfirmar] = useState(false);
  const [estado, setEstado] = useState<EstadoGuardado>(null);
  const [detalle, setDetalle] = useState('');
  const [resultado, setResultado] = useState<{ creados: number; errores: string[] } | null>(null);
  const [error, setError] = useState('');

  const revision = useMemo(() => (filas.length ? revisarFilas(filas, mapeo, contexto) : null), [filas, mapeo, contexto]);
  const faltan = COLUMNAS_IMPORT.filter(c => c.requerido && !Object.values(mapeo).includes(c.campo));
  const aImportar = revision ? revision.filas.filter(f => f.errores.length === 0 && (conAvisos || f.avisos.length === 0)) : [];

  const leer = async (f: File) => {
    setError(''); setResultado(null); setConfirmar(false);
    try {
      const { encabezados: h, filas: fs } = leerHoja(await f.arrayBuffer());
      if (!h.length) { setError('El archivo no tiene encabezados en la primera fila.'); return; }
      setNombre(f.name); setEncabezados(h); setFilas(fs); setMapeo(mapeoAutomatico(h));
    } catch { setError('No se pudo leer el archivo. Usa la plantilla en Excel (.xlsx) o un CSV.'); }
  };

  const importar = async () => {
    setEstado('guardando'); setDetalle('');
    try {
      const r = await importarActivos(companyId, aImportar.map(f => ({ numero: f.numero, datos: f.datos })));
      setResultado(r); setEstado(r.errores.length ? 'error' : 'guardado'); setDetalle(r.errores.length ? `${r.errores.length} filas no se importaron` : '');
      setConfirmar(false); setFilas([]); setEncabezados([]);
      if (r.creados) onImportado();
    } catch (e) { setEstado('error'); setDetalle(msg(e)); }
  };

  return (
    <div className="pe2 mant-ficha">
      <section className="pe2-card">
        <h3>1. Plantilla</h3>
        <p className="pe2-sub">Descarga la plantilla, llénala (una fila por activo) y súbela. También sirve tu propio Excel o CSV: después eliges qué columna es cada dato.</p>
        <div className="pe2-acciones" style={{ justifyContent: 'flex-start' }}>
          <button type="button" className="pe2-btn" onClick={() => descargarLibro(libroPlantilla(), 'Plantilla_inventario_activos.xlsx')}>Descargar plantilla</button>
        </div>
      </section>
      <section className="pe2-card">
        <h3>2. Archivo</h3>
        <label className="pe2-campo">
          <span>Excel (.xlsx) o CSV</span>
          <input type="file" accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) leer(f); }} />
        </label>
        {nombre && <p className="pe2-nota">{nombre}: {filas.length} filas leídas.</p>}
        {error && <div className="pe2-error" role="alert">{error}</div>}
      </section>
      {encabezados.length > 0 && (
        <section className="pe2-card">
          <h3>3. Relación de columnas</h3>
          <div className="pe2-grid">
            {encabezados.map(h => (
              <label key={h} className="pe2-campo">
                <span>Columna «{h}»</span>
                <select value={mapeo[h] ?? ''} onChange={e => setMapeo(m => {
                  const n = { ...m };
                  const campo = e.target.value as CampoImport | '';
                  if (campo) Object.keys(n).forEach(k => { if (n[k] === campo) n[k] = ''; });
                  n[h] = campo;
                  return n;
                })}>
                  <option value="">No importar</option>
                  {COLUMNAS_IMPORT.map(c => <option key={c.campo} value={c.campo}>{c.encabezado}{c.requerido ? ' *' : ''}</option>)}
                </select>
              </label>
            ))}
          </div>
          {faltan.length > 0 && <div className="pe2-aviso">Falta elegir la columna de: {faltan.map(c => c.encabezado).join(', ')}.</div>}
        </section>
      )}
      {revision && faltan.length === 0 && (
        <section className="pe2-card">
          <h3>4. Vista previa</h3>
          <div className="pe2-res">
            <div><b>{revision.validas}</b><span>Listas para importar</span></div>
            <div><b className={revision.conErrores ? 'insp-num-alerta' : undefined}>{revision.conErrores}</b><span>Con errores (no se importan)</span></div>
            <div><b>{revision.conAvisos}</b><span>Con avisos (posible duplicado)</span></div>
          </div>
          {revision.conAvisos > 0 && (
            <label className="pe2-check">
              <input type="checkbox" checked={conAvisos} onChange={e => setConAvisos(e.target.checked)} />
              Importar también las filas con número de serie repetido
            </label>
          )}
          <div className="mant-previa" role="table" aria-label="Vista previa de la importación">
            {revision.filas.slice(0, MAX_PREVIA).map(f => (
              <div key={f.numero} role="row" className={f.errores.length ? 'error' : f.avisos.length ? 'aviso' : 'ok'}>
                <span role="cell">Fila {f.numero}</span>
                <span role="cell"><b>{f.datos.code || 'Código automático'}</b> · {f.datos.name || '—'}{f.datos.category ? ` · ${etiquetaCategoria(f.datos.category)}` : ''}</span>
                <span role="cell">
                  {f.errores.length === 0 && f.avisos.length === 0 && 'Lista'}
                  {f.errores.map(e => <span key={e} className="tab-error">Error: {e} </span>)}
                  {f.avisos.map(a => <span key={a} className="mant-aviso-txt">Aviso: {a} </span>)}
                </span>
              </div>
            ))}
          </div>
          {revision.filas.length > MAX_PREVIA && <p className="pe2-nota">Se muestran las primeras {MAX_PREVIA} filas de {revision.filas.length}.</p>}
          {!confirmar ? (
            <div className="pe2-acciones">
              <button type="button" className="pe2-btn primario" disabled={aImportar.length === 0} onClick={() => setConfirmar(true)}>
                Importar {aImportar.length} {aImportar.length === 1 ? 'activo' : 'activos'}</button>
            </div>
          ) : (
            <div className="tab-confirmar" role="alertdialog" aria-label="Confirmar la importación">
              <p>¿Importar {aImportar.length} {aImportar.length === 1 ? 'activo' : 'activos'}? Las filas con errores no se importan. Los códigos vacíos se asignan solos.</p>
              <button type="button" className="pe2-btn primario" disabled={estado === 'guardando'} onClick={importar}>{estado === 'guardando' ? 'Importando…' : 'Sí, importar'}</button>
              <button type="button" className="pe2-btn" onClick={() => setConfirmar(false)}>Cancelar</button>
            </div>
          )}
        </section>
      )}
      <GuardadoVisible estado={estado} detalle={detalle} />
      {resultado && (
        <section className="pe2-card">
          <p className="pe2-ok">Se importaron {resultado.creados} {resultado.creados === 1 ? 'activo' : 'activos'}.</p>
          {resultado.errores.length > 0 && <ul className="mant-lista">{resultado.errores.map(e => <li key={e} className="tab-error">{e}</li>)}</ul>}
        </section>
      )}
    </div>
  );
}
